import type { Edge, Node } from "@xyflow/react";

import type { GtmBrain, GtmBrainDoc, GtmBrainDocType } from "../api/GtmApi";

/** Knowledge categories shown in sidebar + category hubs on the graph. */
export type BrainCategoryId =
  | "market"
  | "personas"
  | "pillars"
  | "objections"
  | "competitors"
  | "influencers"
  | "keywords"
  | "voice"
  | "signals";

/** Maps overview category → editor accordion SectionId(s). */
export const CATEGORY_TO_SECTION: Record<BrainCategoryId, string> = {
  market: "market",
  personas: "personas",
  pillars: "pillars",
  objections: "objections",
  competitors: "competitors",
  influencers: "influencers",
  keywords: "keywords",
  voice: "voice",
  signals: "signals",
};

export type BrainCategoryMeta = {
  id: BrainCategoryId;
  label: string;
  color: string;
  /** Soft fill for category hub */
  fill: string;
};

export const BRAIN_CATEGORIES: BrainCategoryMeta[] = [
  { id: "market", label: "Market profile", color: "#e11d48", fill: "#ffe4e6" },
  { id: "personas", label: "Personas", color: "#7c3aed", fill: "#ede9fe" },
  { id: "pillars", label: "Message pillars", color: "#2563eb", fill: "#dbeafe" },
  { id: "objections", label: "Objections and responses", color: "#475569", fill: "#e2e8f0" },
  { id: "competitors", label: "Competitors", color: "#ea580c", fill: "#ffedd5" },
  { id: "influencers", label: "Influencers and experts", color: "#ca8a04", fill: "#fef9c3" },
  { id: "keywords", label: "Keywords", color: "#65a30d", fill: "#ecfccb" },
  { id: "voice", label: "Voice", color: "#0d9488", fill: "#ccfbf1" },
  { id: "signals", label: "Company signals", color: "#a16207", fill: "#fef3c7" },
];

export type BrainLeaf = {
  id: string;
  label: string;
  categoryId: BrainCategoryId;
  docId?: string;
  /** Market profile document id when category is market. */
  marketDocId?: "overview" | "icp" | "company";
  /** Voice document id when category is voice. */
  voiceDocId?: "tone" | "linkedin" | "blog";
  /** Company signals document id when category is signals. */
  signalDocId?: "hiring" | "stack";
};

export type BrainFlowNodeData = {
  kind: "hub" | "category" | "leaf";
  label: string;
  categoryId?: BrainCategoryId;
  docId?: string;
  marketDocId?: "overview" | "icp" | "company";
  voiceDocId?: "tone" | "linkedin" | "blog";
  signalDocId?: "hiring" | "stack";
  color?: string;
  fill?: string;
  count?: number;
  selected?: boolean;
};

export type BrainKnowledgeRow = {
  id: BrainCategoryId;
  label: string;
  count: number;
  color: string;
};

const CATEGORY_RING = 300;
const LEAF_RING = 140;
const HUB_X = 0;
const HUB_Y = 0;

function nonEmpty(value?: string | null): boolean {
  return !!value && value.trim().length > 0;
}

/** Market profile always has three documents (Overview / ICP / Company). */
function marketLeaves(_brain: GtmBrain): BrainLeaf[] {
  return [
    { id: "overview", label: "Overview", categoryId: "market", marketDocId: "overview" },
    { id: "icp", label: "ICP", categoryId: "market", marketDocId: "icp" },
    { id: "company", label: "Company", categoryId: "market", marketDocId: "company" },
  ];
}

/** Voice always has three documents (Tone / LinkedIn / Blog). */
function voiceLeaves(_brain: GtmBrain): BrainLeaf[] {
  return [
    { id: "tone", label: "Tone of voice", categoryId: "voice", voiceDocId: "tone" },
    { id: "linkedin", label: "LinkedIn posts", categoryId: "voice", voiceDocId: "linkedin" },
    { id: "blog", label: "Blog articles", categoryId: "voice", voiceDocId: "blog" },
  ];
}

/** Company signals always has two documents (Hiring / Stack). */
function signalLeaves(_brain: GtmBrain): BrainLeaf[] {
  return [
    { id: "hiring", label: "Hiring", categoryId: "signals", signalDocId: "hiring" },
    { id: "stack", label: "Stack", categoryId: "signals", signalDocId: "stack" },
  ];
}

function docLeaves(docs: GtmBrainDoc[], docType: GtmBrainDocType, categoryId: BrainCategoryId): BrainLeaf[] {
  return docs
    .filter((d) => d.docType === docType)
    .map((d, i) => {
      const label =
        (d.name || d.phrase || d.objection || d.pain || d.notes || `${docType} ${i + 1}`).trim() ||
        `${docType} ${i + 1}`;
      const id = d.id || `doc-${docType}-${i}`;
      return {
        id,
        label: label.length > 40 ? `${label.slice(0, 37)}…` : label,
        categoryId,
        // Prefer real id; fall back to stable synthetic key for graph → list routing.
        docId: id,
      };
    });
}

export function leavesForCategory(brain: GtmBrain, docs: GtmBrainDoc[], categoryId: BrainCategoryId): BrainLeaf[] {
  switch (categoryId) {
    case "market":
      return marketLeaves(brain);
    case "personas":
      return docLeaves(docs, "persona", "personas");
    case "pillars":
      return docLeaves(docs, "pillar", "pillars");
    case "objections":
      return docLeaves(docs, "objection", "objections");
    case "competitors":
      return docLeaves(docs, "competitor", "competitors");
    case "influencers":
      return docLeaves(docs, "influencer", "influencers");
    case "keywords":
      return docLeaves(docs, "keyword", "keywords");
    case "voice":
      return voiceLeaves(brain);
    case "signals":
      return signalLeaves(brain);
    default:
      return [];
  }
}

export function buildKnowledgeRows(brain: GtmBrain, docs: GtmBrainDoc[]): BrainKnowledgeRow[] {
  return BRAIN_CATEGORIES.map((c) => ({
    id: c.id,
    label: c.label,
    // Market / Voice / Signals count fixed documents, not field chips.
    count:
      c.id === "market" || c.id === "voice"
        ? 3
        : c.id === "signals"
          ? 2
          : leavesForCategory(brain, docs, c.id).length,
    color: c.color,
  }));
}

function polar(cx: number, cy: number, r: number, angleRad: number) {
  return { x: cx + r * Math.cos(angleRad), y: cy + r * Math.sin(angleRad) };
}

export type BrainToFlowOptions = {
  selectedCategoryId?: BrainCategoryId | null;
  /** Highlight the open leaf (doc / market / voice / signal id). */
  selectedLeafId?: string | null;
  query?: string;
};

/**
 * Build a radial xyflow graph: hub → category hubs → leaf dots.
 */
export function brainToFlow(
  brain: GtmBrain,
  docs: GtmBrainDoc[],
  options: BrainToFlowOptions = {}
): { nodes: Node<BrainFlowNodeData>[]; edges: Edge[]; knowledge: BrainKnowledgeRow[] } {
  const query = (options.query || "").trim().toLowerCase();
  const knowledge = buildKnowledgeRows(brain, docs);
  const hubLabel =
    (brain.companyName || "").trim() ||
    (brain.domain || "").trim() ||
    "Brain";

  const nodes: Node<BrainFlowNodeData>[] = [];
  const edges: Edge[] = [];

  nodes.push({
    id: "hub",
    type: "brainHub",
    position: { x: HUB_X - 70, y: HUB_Y - 28 },
    data: { kind: "hub", label: hubLabel },
    draggable: false,
    selectable: false,
  });

  const n = BRAIN_CATEGORIES.length;
  BRAIN_CATEGORIES.forEach((cat, i) => {
    const leaves = leavesForCategory(brain, docs, cat.id).filter((leaf) => {
      if (!query) return true;
      return (
        cat.label.toLowerCase().includes(query) ||
        leaf.label.toLowerCase().includes(query)
      );
    });
    const categoryMatches = !query || cat.label.toLowerCase().includes(query) || leaves.length > 0;
    if (query && !categoryMatches) return;

    const angle = (i / n) * Math.PI * 2 - Math.PI / 2;
    const pos = polar(HUB_X, HUB_Y, CATEGORY_RING, angle);
    const catNodeId = `cat-${cat.id}`;
    const selected = options.selectedCategoryId === cat.id;

    nodes.push({
      id: catNodeId,
      type: "brainCategory",
      position: { x: pos.x - 56, y: pos.y - 22 },
      data: {
        kind: "category",
        label: cat.label,
        categoryId: cat.id,
        color: cat.color,
        fill: cat.fill,
        count: leavesForCategory(brain, docs, cat.id).length,
        selected,
      },
      draggable: false,
    });

    edges.push({
      id: `e-hub-${cat.id}`,
      source: "hub",
      target: catNodeId,
      type: "straight",
      style: { stroke: "#cbd5e1", strokeWidth: 1.5 },
    });

    const leafCount = leaves.length;
    leaves.forEach((leaf, li) => {
      const spread = leafCount === 1 ? 0 : ((li / (leafCount - 1)) - 0.5) * (Math.PI / 3);
      const leafAngle = angle + spread;
      const leafPos = polar(pos.x, pos.y, LEAF_RING, leafAngle);
      const leafId = `leaf-${cat.id}-${leaf.id}`;
      const leafSelected =
        !!options.selectedLeafId &&
        (leaf.id === options.selectedLeafId ||
          leaf.docId === options.selectedLeafId ||
          leaf.marketDocId === options.selectedLeafId ||
          leaf.voiceDocId === options.selectedLeafId ||
          leaf.signalDocId === options.selectedLeafId);
      nodes.push({
        id: leafId,
        type: "brainLeaf",
        position: { x: leafPos.x - 74, y: leafPos.y - 14 },
        data: {
          kind: "leaf",
          label: leaf.label,
          categoryId: cat.id,
          docId: leaf.docId,
          marketDocId: leaf.marketDocId,
          voiceDocId: leaf.voiceDocId,
          signalDocId: leaf.signalDocId,
          color: cat.color,
          fill: cat.fill,
          selected: leafSelected,
        },
        draggable: false,
      });
      edges.push({
        id: `e-${cat.id}-${leaf.id}`,
        source: catNodeId,
        target: leafId,
        type: "straight",
        style: { stroke: "#e2e8f0", strokeWidth: 1 },
      });
    });
  });

  return { nodes, edges, knowledge };
}
